import React, { useState, useEffect } from 'react';
import { Package, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import StatCard from '@/components/dashboard/StatCard';
import { useAuth } from '@/context/AuthContext';
import { listarEntregas, atualizarStatusEntrega } from '@/services/api';
import api from '@/services/api';

const DashboardFuncionario = () => {
  const { usuario, empresa } = useAuth();
  const [entregas, setEntregas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openConfirmar, setOpenConfirmar] = useState(false);
  const [entregaParaConfirmar, setEntregaParaConfirmar] = useState(null);
  const [codigoConfirmacao, setCodigoConfirmacao] = useState('');
  
  useEffect(() => {
    carregarEntregas();
  }, []);
  
  const carregarEntregas = async () => {
    try {
      const response = await listarEntregas({ entregador_id: empresa.id });
      setEntregas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar entregas');
    }
  };
  
  const handleAceitarEntrega = async (entregaId) => {
    setLoading(true);
    try {
      await atualizarStatusEntrega(entregaId, {
        status: 'aceita',
        funcionario_id: usuario.id
      });
      toast.success('Entrega aceita!');
      carregarEntregas();
    } catch (error) {
      toast.error('Erro ao aceitar entrega');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAtualizarStatus = async (entregaId, novoStatus) => {
    setLoading(true);
    try {
      await atualizarStatusEntrega(entregaId, { status: novoStatus });
      toast.success('Status atualizado!');
      carregarEntregas();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirmarEntrega = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post(`/entregas/${entregaParaConfirmar.id}/confirmar`, {
        codigo: codigoConfirmacao
      });
      
      toast.success('Entrega confirmada com sucesso!');
      setOpenConfirmar(false);
      setCodigoConfirmacao('');
      setEntregaParaConfirmar(null);
      carregarEntregas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };
  
  const minhasEntregas = entregas.filter(e => e.funcionario_id === usuario.id);
  
  return (
    <DashboardLayout title="Dashboard Funcionário">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard title="Minhas Entregas" value={minhasEntregas.length} icon={Package} color="blue" />
        <StatCard
          title="Finalizadas"
          value={minhasEntregas.filter(e => e.status === 'entregue').length}
          icon={CheckCircle2}
          color="emerald"
        />
      </div>
      
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Entregas Disponíveis</h2>
        </div>
        <div className="space-y-4 p-6">
          {entregas.filter(e => !e.funcionario_id || e.funcionario_id === usuario.id).map((e) => (
            <div key={e.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-slate-900">{e.descricao}</h3>
                  <p className="text-sm text-slate-600 mt-1">{e.endereco_origem} → {e.endereco_destino}</p>
                  <p className="text-xs text-slate-500 mt-1">Status: {e.status}</p>
                </div>
                <div className="flex gap-2">
                  {!e.funcionario_id && (
                    <Button size="sm" onClick={() => handleAceitarEntrega(e.id)} disabled={loading}>
                      Aceitar
                    </Button>
                  )}
                  {e.funcionario_id === usuario.id && e.status === 'aceita' && (
                    <Button size="sm" onClick={() => handleAtualizarStatus(e.id, 'em_rota')} disabled={loading}>
                      Iniciar Rota
                    </Button>
                  )}
                  {e.funcionario_id === usuario.id && e.status === 'em_rota' && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => handleAtualizarStatus(e.id, 'entregue')} 
                        disabled={loading} 
                        className="bg-amber-600"
                      >
                        Marcar Entregue
                      </Button>
                    </>
                  )}
                  {e.funcionario_id === usuario.id && e.status === 'entregue' && !e.confirmada_em && (
                    <Button 
                      size="sm" 
                      onClick={() => {
                        setEntregaParaConfirmar(e);
                        setOpenConfirmar(true);
                      }}
                      disabled={loading} 
                      className="bg-emerald-600"
                    >
                      Confirmar com Código
                    </Button>
                  )}
                  {e.confirmada_em && (
                    <span className="text-xs text-emerald-600 font-semibold">✓ Confirmada</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Modal Confirmar Entrega */}
      <Dialog open={openConfirmar} onOpenChange={setOpenConfirmar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Entrega</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleConfirmarEntrega} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Solicite o <strong>código de confirmação de 6 dígitos</strong> ao cliente para finalizar a entrega.
              </p>
            </div>
            
            {entregaParaConfirmar && (
              <div>
                <p className="text-sm text-slate-600 mb-2">Entrega: {entregaParaConfirmar.descricao}</p>
                <p className="text-xs text-slate-500">Destino: {entregaParaConfirmar.endereco_destino}</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Código de Confirmação
              </label>
              <Input
                type="text"
                placeholder="000000"
                value={codigoConfirmacao}
                onChange={(e) => setCodigoConfirmacao(e.target.value)}
                maxLength={6}
                required
                className="text-center text-2xl font-mono tracking-widest"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpenConfirmar(false);
                  setCodigoConfirmacao('');
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || codigoConfirmacao.length !== 6}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Confirmando...' : 'Confirmar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default DashboardFuncionario;
